import * as React from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';

// Global event to close all dropdowns
const CLOSE_ALL_DROPDOWNS = 'closeAllDropdowns';

interface DropdownMenuContextValue {
    open: boolean;
    setOpen: (open: boolean) => void;
    triggerRef: React.RefObject<HTMLDivElement | null>;
    dropdownId: string;
}

const DropdownMenuContext = React.createContext<DropdownMenuContextValue | undefined>(undefined);

export function useDropdownMenu() {
    const context = React.useContext(DropdownMenuContext);
    if (!context) {
        throw new Error('useDropdownMenu must be used within a DropdownMenu');
    }
    return context;
}

interface DropdownMenuProps {
    children: React.ReactNode;
    /** Extra classes for the wrapper, e.g. to let it fill its row. */
    className?: string;
}

let dropdownCounter = 0;

export function DropdownMenu({ children, className }: DropdownMenuProps) {
    const [open, setOpenState] = React.useState(false);
    const triggerRef = React.useRef<HTMLDivElement>(null);
    const dropdownId = React.useRef(`dropdown-${++dropdownCounter}`).current;

    // Custom setOpen that broadcasts close event to other dropdowns
    const setOpen = React.useCallback((newOpen: boolean) => {
        if (newOpen) {
            // Close all other dropdowns before opening this one
            window.dispatchEvent(new CustomEvent(CLOSE_ALL_DROPDOWNS, { detail: { exceptId: dropdownId } }));
        }
        setOpenState(newOpen);
    }, [dropdownId]);

    // Listen for close events from other dropdowns
    React.useEffect(() => {
        const handleCloseAll = (e: Event) => {
            const customEvent = e as CustomEvent<{ exceptId: string }>;
            if (customEvent.detail.exceptId !== dropdownId) {
                setOpenState(false);
            }
        };

        window.addEventListener(CLOSE_ALL_DROPDOWNS, handleCloseAll);
        return () => window.removeEventListener(CLOSE_ALL_DROPDOWNS, handleCloseAll);
    }, [dropdownId]);

    // Close on click outside
    React.useEffect(() => {
        if (!open) return;

        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            // Check if click is inside THIS dropdown's trigger or content
            const isInsideThisDropdown = triggerRef.current?.contains(target);
            const isInsideThisContent = target.closest(`[data-dropdown-id="${dropdownId}"]`);

            if (!isInsideThisDropdown && !isInsideThisContent) {
                setOpenState(false);
            }
        };

        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setOpenState(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEscape);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscape);
        };
    }, [open, dropdownId]);

    return (
        <DropdownMenuContext.Provider value={{ open, setOpen, triggerRef, dropdownId }}>
            <div className={cn('relative inline-block', className)} data-dropdown-menu ref={triggerRef}>
                {children}
            </div>
        </DropdownMenuContext.Provider>
    );
}

interface DropdownMenuTriggerProps {
    children: React.ReactNode;
    asChild?: boolean;
}

export function DropdownMenuTrigger({ children, asChild }: DropdownMenuTriggerProps) {
    const { open, setOpen } = useDropdownMenu();

    const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        setOpen(!open);
    };

    if (asChild && React.isValidElement(children)) {
        return React.cloneElement(children as React.ReactElement<{ onClick?: (e: React.MouseEvent) => void }>, {
            onClick: (e: React.MouseEvent) => {
                handleClick(e);
                (children as React.ReactElement<{ onClick?: (e: React.MouseEvent) => void }>).props.onClick?.(e);
            },
        });
    }

    return (
        <button onClick={handleClick} type="button">
            {children}
        </button>
    );
}

interface DropdownMenuContentProps {
    children: React.ReactNode;
    align?: 'start' | 'center' | 'end';
    className?: string;
}

export function DropdownMenuContent({
    children,
    align = 'end',
    className,
}: DropdownMenuContentProps) {
    const { open, triggerRef, dropdownId } = useDropdownMenu();
    const [position, setPosition] = React.useState({ top: 0, left: 0 });
    const contentRef = React.useRef<HTMLDivElement>(null);

    React.useLayoutEffect(() => {
        if (!open || !triggerRef.current) return;

        const updatePosition = () => {
            const rect = triggerRef.current!.getBoundingClientRect();
            const contentWidth = contentRef.current?.offsetWidth || 160;

            let left = rect.right - contentWidth; // align end by default
            if (align === 'start') {
                left = rect.left;
            } else if (align === 'center') {
                left = rect.left + rect.width / 2 - contentWidth / 2;
            }

            // Ensure it doesn't go off-screen
            left = Math.max(8, Math.min(left, window.innerWidth - contentWidth - 8));

            setPosition({
                top: rect.bottom + window.scrollY + 4,
                left: left + window.scrollX,
            });
        };

        updatePosition();
        window.addEventListener('scroll', updatePosition, true);
        window.addEventListener('resize', updatePosition);

        return () => {
            window.removeEventListener('scroll', updatePosition, true);
            window.removeEventListener('resize', updatePosition);
        };
    }, [open, align, triggerRef]);

    if (!open) return null;

    return createPortal(
        <div
            ref={contentRef}
            data-dropdown-content
            data-dropdown-id={dropdownId}
            style={{ position: 'absolute', top: position.top, left: position.left }}
            className={cn(
                'z-50 min-w-[160px] rounded-lg bg-white dark:bg-dark-card py-1 shadow-lg ring-1 ring-black/5 dark:ring-dark-border',
                'animate-in fade-in-0 zoom-in-95',
                className
            )}
        >
            {children}
        </div>,
        document.body
    );
}

interface DropdownMenuItemProps {
    children: React.ReactNode;
    onClick?: () => void;
    className?: string;
    disabled?: boolean;
    keepOpen?: boolean;
}

export function DropdownMenuItem({
    children,
    onClick,
    className,
    disabled,
    keepOpen,
}: DropdownMenuItemProps) {
    const { setOpen } = useDropdownMenu();

    const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        if (disabled) return;
        onClick?.();
        if (!keepOpen) setOpen(false);
    };

    return (
        <button
            type="button"
            onClick={handleClick}
            disabled={disabled}
            className={cn(
                'flex w-full items-center gap-2 px-3 py-2 text-[13px] text-dark dark:text-dark-text truncate',
                'hover:bg-surface dark:hover:bg-dark-hover transition-colors',
                'disabled:opacity-50 disabled:cursor-not-allowed',
                className
            )}
        >
            {children}
        </button>
    );
}

export function DropdownMenuSeparator() {
    return <div className="my-1 h-px bg-bordergray dark:bg-dark-border" />;
}

interface DropdownMenuLabelProps {
    children: React.ReactNode;
    className?: string;
}

export function DropdownMenuLabel({ children, className }: DropdownMenuLabelProps) {
    return (
        <div
            className={cn(
                'px-3 py-1.5 text-[11px] font-semibold text-muted dark:text-dark-muted uppercase tracking-wider',
                className
            )}
        >
            {children}
        </div>
    );
}
